import { Tabs } from "react-migration/components/Tabs";
import { Loading } from "react-migration/components/Loading";
import React from "react";
import { Icon } from "react-migration/components/Icon";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Button } from "react-migration/components/Button";
import { ScopeType } from "src/js/stores/site/store";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";

type CardResultsType = {
  filtered: number;
  all: number;
  limit?: number;
  loading?: boolean;
};
interface CardTabsProps {
  tab: ScopeType;
  onTabChange: (value: ScopeType) => void;
  resultsCountName?: string;
  siteResults: CardResultsType;
  nearbyResults: CardResultsType;
  children?: React.ReactNode;
}

interface TabContentProps {
  error: boolean;
  loading: boolean;
  resultsCount: number;
  setTab?: React.Dispatch<React.SetStateAction<ScopeType>>;
  children: React.ReactNode;
}

interface NearbyTabProps extends TabContentProps {
  radiusInput: React.ReactNode;
}

interface NoResultsProps {
  nearby?: boolean;
  setTab?: React.Dispatch<React.SetStateAction<ScopeType>>;
}

const pluralize = (count: number, all: number, resultsLimit: number | undefined, name: string) =>
  `${count} of ${all}${resultsLimit && all === resultsLimit ? "+" : ""} ${name}${
    all === 1 || name.length === 0 ? "" : "s"
  }`;

const NoResults = ({ nearby = false, setTab }: NoResultsProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-center gap-2 p-10 text-center bg-background-secondary">
      <div className="p-2.5 mb-6">
        <Icon icon="icon-lt-seo-line" size="lg" />
      </div>
      <h5 className="text-lg text-blue-grey-950">
        {nearby
          ? t("workbench.contextual_tab.nearby.no_considerations.title")
          : t("workbench.contextual_tab.onsite.no_considerations.title")}
      </h5>
      <p className="m-0 text-sm text-blue-grey-950">
        {nearby
          ? t("workbench.contextual_tab.nearby.no_considerations.description")
          : t("workbench.contextual_tab.onsite.no_considerations.description")}
      </p>

      {!nearby && (
        <Button
          variant="tertiary"
          size="md"
          className="mt-2 text-xs text-text-base-secondary"
          tailIcon="lt-arrow-right-line"
          onClick={() => setTab && setTab(ScopeType.NEARBY)}
        >
          {t("workbench.contextual_tab.no_considerations.cta")}
        </Button>
      )}
    </div>
  );
};

const SiteTab = ({ error, loading, resultsCount, setTab, children }: TabContentProps) => (
  <Tabs.Content value={ScopeType.ON_SITE} className="flex flex-col w-full min-h-0">
    {loading && (
      <div className="flex items-center justify-center p-4">
        <Loading />
      </div>
    )}
    {!error && !loading && children}
    {!error && !loading && !resultsCount && <NoResults setTab={setTab} />}
  </Tabs.Content>
);

const NearbyTab = ({ error, loading, resultsCount, children, radiusInput }: NearbyTabProps) => (
  <Tabs.Content value={ScopeType.NEARBY} className="flex flex-col w-full min-h-0">
    <div className="flex items-center justify-between p-2">{radiusInput}</div>
    {loading && (
      <div className="flex items-center justify-center p-4">
        <Loading />
      </div>
    )}
    {!error && !loading && children}
    {!error && !loading && !resultsCount && <NoResults nearby />}
  </Tabs.Content>
);

export const CardTabs = ({
  tab,
  onTabChange,
  resultsCountName = "",
  siteResults,
  nearbyResults,
  children,
}: CardTabsProps) => {
  const { t } = useTranslation();

  const handleTabChange = (value: ScopeType) => {
    logEventV2({
      name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
      properties: {
        cardName: "comparables",
        action: "tab click",
        clickDetail: value,
      },
    });
    onTabChange(value);
  };

  return (
    <Tabs value={tab} onValueChange={handleTabChange} className="min-h-0 bg-transparent">
      <Tabs.List>
        <Tabs.Trigger value={ScopeType.ON_SITE} data-testid="onsite-tab" className="px-4! py-2! items-start! gap-2">
          <div>{t("workbench.contextual_tab.onsite")}</div>
          <span className="text-3xs text-text-base-tertiary">
            {siteResults.loading ? (
              <Loading relativeSizing={true} />
            ) : (
              pluralize(siteResults.filtered, siteResults.all, siteResults.limit, resultsCountName)
            )}
          </span>
        </Tabs.Trigger>
        <Tabs.Trigger value={ScopeType.NEARBY} data-testid="nearby-tab" className="px-4! py-2! items-start! gap-2">
          <div>{t("workbench.contextual_tab.nearby")}</div>
          <span className="text-3xs text-text-base-tertiary">
            {nearbyResults.loading ? (
              <Loading relativeSizing={true} />
            ) : (
              pluralize(nearbyResults.filtered, nearbyResults.all, nearbyResults.limit, resultsCountName)
            )}
          </span>
        </Tabs.Trigger>
      </Tabs.List>
      {children}
    </Tabs>
  );
};

CardTabs.SiteTab = SiteTab;
CardTabs.NearbyTab = NearbyTab;
